const VERTEX_SHADER_SOURCE = `
attribute vec2 a_position;
varying vec2 v_uv;

void main() {
  v_uv = a_position * 0.5 + 0.5;
  gl_Position = vec4(a_position, 0.0, 1.0);
}
`;

const FRAGMENT_SHADER_SOURCE = `
precision mediump float;

uniform vec2 u_resolution;
uniform vec2 u_logo_resolution;
uniform float u_logo_draw_scale;
uniform float u_time;
uniform sampler2D u_logo;
uniform vec3 u_mark;
uniform vec3 u_wisp;
uniform vec3 u_logo_pastel_a;
uniform vec3 u_logo_pastel_b;
uniform vec3 u_logo_pastel_c;
uniform vec2 u_pointer;
uniform vec2 u_pointer_velocity;
uniform float u_pointer_strength;
varying vec2 v_uv;

float hash(vec2 point) {
  vec3 value = fract(vec3(point.xyx) * 0.1031);
  value += dot(value, value.yzx + 33.33);
  return fract((value.x + value.y) * value.z);
}

float noise(vec2 point) {
  vec2 cell = floor(point);
  vec2 local = fract(point);
  vec2 curve = local * local * (3.0 - 2.0 * local);
  float a = hash(cell);
  float b = hash(cell + vec2(1.0, 0.0));
  float c = hash(cell + vec2(0.0, 1.0));
  float d = hash(cell + vec2(1.0, 1.0));

  return mix(mix(a, b, curve.x), mix(c, d, curve.x), curve.y);
}

float fbm(vec2 point) {
  mat2 turn = mat2(0.8, -0.6, 0.6, 0.8);
  float value = 0.0;
  float amplitude = 0.5;

  for (int i = 0; i < 4; i += 1) {
    value += amplitude * noise(point);
    point = turn * point * 2.04 + 5.71;
    amplitude *= 0.52;
  }

  return value;
}

vec2 curl(vec2 point) {
  float epsilon = 0.035;
  float left = fbm(point - vec2(epsilon, 0.0));
  float right = fbm(point + vec2(epsilon, 0.0));
  float down = fbm(point - vec2(0.0, epsilon));
  float up = fbm(point + vec2(0.0, epsilon));

  return vec2(up - down, left - right) / (2.0 * epsilon);
}

vec3 colorRamp(float amount) {
  float wave = 0.5 + 0.5 * sin(amount * 6.2831);
  float drift = 0.5 + 0.5 * sin(amount * 4.7 + 1.4);
  vec3 first = mix(u_logo_pastel_a, u_logo_pastel_b, wave);
  vec3 second = mix(first, u_logo_pastel_c, 0.3 + drift * 0.24);

  return mix(second, u_mark, 0.04);
}

vec2 containedLogoUv(vec2 uv) {
  float canvasAspect = u_resolution.x / max(u_resolution.y, 1.0);
  float logoAspect = u_logo_resolution.x / max(u_logo_resolution.y, 1.0);
  float drawScale = clamp(u_logo_draw_scale, 0.12, 1.0);
  vec2 logoUv = uv;

  if (canvasAspect > logoAspect) {
    float drawWidth = logoAspect / canvasAspect * drawScale;
    logoUv.x = (uv.x - 0.5) / drawWidth + 0.5;
    logoUv.y = (uv.y - 0.5) / drawScale + 0.5;
  } else {
    float drawHeight = canvasAspect / logoAspect * drawScale;
    logoUv.x = (uv.x - 0.5) / drawScale + 0.5;
    logoUv.y = (uv.y - 0.5) / drawHeight + 0.5;
  }

  return logoUv;
}

float sampleLogoAlpha(vec2 uv) {
  if (uv.x < 0.0 || uv.x > 1.0 || uv.y < 0.0 || uv.y > 1.0) {
    return 0.0;
  }

  return texture2D(u_logo, uv).a;
}

float edgeAlpha(vec2 uv, vec2 pixel) {
  float center = sampleLogoAlpha(uv);
  float east = sampleLogoAlpha(uv + vec2(pixel.x, 0.0));
  float west = sampleLogoAlpha(uv - vec2(pixel.x, 0.0));
  float north = sampleLogoAlpha(uv + vec2(0.0, pixel.y));
  float south = sampleLogoAlpha(uv - vec2(0.0, pixel.y));

  return length(vec2(east - west, north - south)) + center * 0.08;
}

float softLogoInfluence(vec2 uv, vec2 pixel) {
  float center = sampleLogoAlpha(uv);
  float nearSignal = 0.0;
  nearSignal = max(nearSignal, sampleLogoAlpha(uv + vec2(pixel.x, 0.0)));
  nearSignal = max(nearSignal, sampleLogoAlpha(uv - vec2(pixel.x, 0.0)));
  nearSignal = max(nearSignal, sampleLogoAlpha(uv + vec2(0.0, pixel.y)));
  nearSignal = max(nearSignal, sampleLogoAlpha(uv - vec2(0.0, pixel.y)));

  float wide = 0.0;
  wide = max(wide, sampleLogoAlpha(uv + vec2(pixel.x, pixel.y) * 2.35));
  wide = max(wide, sampleLogoAlpha(uv + vec2(-pixel.x, pixel.y) * 2.35));
  wide = max(wide, sampleLogoAlpha(uv + vec2(pixel.x, -pixel.y) * 2.35));
  wide = max(wide, sampleLogoAlpha(uv + vec2(-pixel.x, -pixel.y) * 2.35));

  return max(center, max(nearSignal * 0.82, wide * 0.56));
}

void main() {
  vec2 uv = v_uv;
  vec2 logoUv = containedLogoUv(uv);
  vec2 logoPixel = 1.0 / max(u_logo_resolution, vec2(1.0));
  vec2 aspect = vec2(u_resolution.x / max(u_resolution.y, 1.0), 1.0);
  vec2 point = (uv - 0.5) * aspect;
  vec2 pointerPoint = (u_pointer - 0.5) * aspect;
  vec2 pointerDelta = point - pointerPoint;
  float pointerDistance = length(pointerDelta);
  float pointerWake = exp(-pointerDistance * pointerDistance * 0.34) * u_pointer_strength;
  float active = smoothstep(0.004, 0.095, pointerWake);
  vec2 velocity = vec2(u_pointer_velocity.x * aspect.x, u_pointer_velocity.y);
  float speed = clamp(length(velocity), 0.0, 2.2);
  vec2 direction = normalize(mix(
    normalize(pointerDelta + vec2(0.001, 0.0)),
    normalize(velocity + vec2(0.001, 0.0)),
    smoothstep(0.035, 0.2, speed)
  ));
  vec2 normal = vec2(-direction.y, direction.x);
  float along = dot(pointerDelta, direction);
  float across = dot(pointerDelta, normal);
  float ribbon = exp(-across * across * 2.2) * exp(-abs(along) * 0.72);
  float wave = sin(along * 7.2 - u_time * 4.4 + fbm(point * 2.4 + u_time * 0.12) * 3.0);
  vec2 organicFlow = curl(point * 1.9 + vec2(u_time * 0.08, -u_time * 0.06)) * 0.013;
  vec2 warp = (
    direction * (0.052 + speed * 0.014)
    + normal * wave * 0.03
    + organicFlow
  ) * pointerWake * (0.86 + ribbon * 0.5);
  vec2 logoPointerUv = containedLogoUv(u_pointer);
  vec2 lensDelta = logoUv - logoPointerUv;
  float lensField = smoothstep(1.9, 0.0, pointerDistance) * active;
  vec2 lensUv = logoUv - lensDelta * lensField * (0.21 + speed * 0.014);
  vec2 warpedUv = lensUv - warp;
  float crispAlpha = sampleLogoAlpha(logoUv);
  float warpedAlpha = sampleLogoAlpha(warpedUv);
  float alpha = mix(crispAlpha, warpedAlpha, active);
  float interactionAlpha = softLogoInfluence(warpedUv, logoPixel * 4.8);
  float edge = edgeAlpha(warpedUv, logoPixel * 1.35);
  float chromaAmount = active * (1.12 + speed * 1.15 + edge * 1.9);
  vec2 chromaOffset = normalize(direction + normal * 0.32) * logoPixel * chromaAmount * 3.0;
  float firstAlpha = sampleLogoAlpha(warpedUv + chromaOffset);
  float secondAlpha = sampleLogoAlpha(warpedUv - chromaOffset * 1.16);
  float floodShape = (
    smoothstep(1.95, 0.0, pointerDistance)
    + active * 0.46
  )
    * u_pointer_strength
    * smoothstep(0.0, 0.58, max(alpha, interactionAlpha * 0.72));
  float floodWave = fbm(point * 5.4 + direction * u_time * 0.36 + normal * wave * 0.22);
  vec3 floodColor = colorRamp(floodWave + along * 1.8 - across * 0.9 + u_time * 0.08);
  float ambientWave = fbm(logoUv * 4.2 + vec2(u_time * 0.055, -u_time * 0.038))
    + sin((logoUv.x + logoUv.y) * 6.4 + u_time * 0.72) * 0.16;
  float ambientSignal = smoothstep(0.32, 0.92, ambientWave) * 0.11 * crispAlpha;
  vec3 ambientColor = colorRamp(ambientWave + u_time * 0.025);
  vec3 restingColor = mix(u_mark, ambientColor, ambientSignal);
  vec3 color = mix(restingColor, floodColor, clamp(floodShape * 1.05, 0.0, 0.96)) * alpha;
  float edgeSplit = smoothstep(0.035, 0.55, edge + abs(firstAlpha - secondAlpha) * 0.55) * active;

  color += u_logo_pastel_a * firstAlpha * edgeSplit * (0.22 + speed * 0.08);
  color += u_logo_pastel_b * secondAlpha * edgeSplit * (0.2 + speed * 0.07);
  alpha = clamp(max(alpha, max(firstAlpha, secondAlpha) * edgeSplit * 0.22), 0.0, 1.0);

  gl_FragColor = vec4(clamp(color, 0.0, 1.0), alpha);
}
`;

const POINTER_VELOCITY_FOLLOW = 0.38;

function clampUnit(value) {
  return Math.max(0, Math.min(1, value));
}

function distanceToUnitSquare(x, y) {
  const nearestX = clampUnit(x);
  const nearestY = clampUnit(y);

  return Math.hypot(x - nearestX, y - nearestY);
}

function clampVelocity(value) {
  return Math.max(-2.2, Math.min(2.2, value));
}

function createShader(gl, type, source) {
  const shader = gl.createShader(type);

  gl.shaderSource(shader, source);
  gl.compileShader(shader);

  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    const error = gl.getShaderInfoLog(shader);
    gl.deleteShader(shader);
    throw new Error(error || 'Failed to compile login logo shader.');
  }

  return shader;
}

function createProgram(gl) {
  const vertexShader = createShader(gl, gl.VERTEX_SHADER, VERTEX_SHADER_SOURCE);
  const fragmentShader = createShader(gl, gl.FRAGMENT_SHADER, FRAGMENT_SHADER_SOURCE);
  const program = gl.createProgram();

  gl.attachShader(program, vertexShader);
  gl.attachShader(program, fragmentShader);
  gl.linkProgram(program);
  gl.deleteShader(vertexShader);
  gl.deleteShader(fragmentShader);

  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    const error = gl.getProgramInfoLog(program);
    gl.deleteProgram(program);
    throw new Error(error || 'Failed to link login logo shader.');
  }

  return program;
}

function createLogoTexture(gl, image) {
  const texture = gl.createTexture();

  if (!texture) {
    throw new Error('Failed to allocate login logo texture.');
  }

  gl.bindTexture(gl.TEXTURE_2D, texture);
  gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);
  gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
  gl.texImage2D(
    gl.TEXTURE_2D,
    0,
    gl.RGBA,
    gl.RGBA,
    gl.UNSIGNED_BYTE,
    image,
  );

  return texture;
}

function setUniformColor(gl, location, color) {
  gl.uniform3f(location, color[0], color[1], color[2]);
}

function getUniforms(gl, program, names) {
  return Object.fromEntries(names.map((name) => [name, gl.getUniformLocation(program, name)]));
}

export function createLoginLogoShaderRuntime({ canvas, gl, image, palette }) {
  const program = createProgram(gl);
  const texture = createLogoTexture(gl, image);
  const positionBuffer = gl.createBuffer();

  if (!positionBuffer) {
    throw new Error('Failed to allocate login logo geometry.');
  }

  const positionAttribute = gl.getAttribLocation(program, 'a_position');
  const uniforms = getUniforms(gl, program, [
    'u_resolution',
    'u_logo_resolution',
    'u_logo_draw_scale',
    'u_time',
    'u_logo',
    'u_mark',
    'u_wisp',
    'u_logo_pastel_a',
    'u_logo_pastel_b',
    'u_logo_pastel_c',
    'u_pointer',
    'u_pointer_velocity',
    'u_pointer_strength',
  ]);
  const logoWidth = image.naturalWidth || image.width || 1;
  const logoHeight = image.naturalHeight || image.height || 1;
  const pointer = {
    x: 0.5,
    y: 0.5,
    targetX: 0.5,
    targetY: 0.5,
    velocityX: 0,
    velocityY: 0,
    targetVelocityX: 0,
    targetVelocityY: 0,
    strength: 0,
    targetStrength: 0,
    lastMove: 0,
  };

  gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer);
  gl.bufferData(
    gl.ARRAY_BUFFER,
    new Float32Array([-1, -1, 3, -1, -1, 3]),
    gl.STATIC_DRAW,
  );

  gl.disable(gl.DEPTH_TEST);
  gl.disable(gl.STENCIL_TEST);

  const resize = () => {
    const pixelRatio = Math.min(window.devicePixelRatio || 1, 2);
    const width = Math.max(1, Math.floor(canvas.clientWidth * pixelRatio));
    const height = Math.max(1, Math.floor(canvas.clientHeight * pixelRatio));

    if (canvas.width !== width || canvas.height !== height) {
      canvas.width = width;
      canvas.height = height;
    }
  };

  const measureLogoDrawScale = () => {
    const logoElement = canvas.nextElementSibling;
    const canvasBounds = canvas.getBoundingClientRect();
    const logoBounds = logoElement?.getBoundingClientRect();

    if (!logoBounds || canvasBounds.width <= 0 || canvasBounds.height <= 0) {
      return 1;
    }

    return Math.max(
      0.12,
      Math.min(1, Math.min(
        logoBounds.width / canvasBounds.width,
        logoBounds.height / canvasBounds.height,
      )),
    );
  };

  const drawFrame = (timestamp = 0, { reducedMotion = false } = {}) => {
    resize();

    pointer.targetVelocityX *= reducedMotion ? 0 : 0.92;
    pointer.targetVelocityY *= reducedMotion ? 0 : 0.92;

    const follow = reducedMotion ? 1 : 0.12;
    pointer.x += (pointer.targetX - pointer.x) * follow;
    pointer.y += (pointer.targetY - pointer.y) * follow;
    pointer.velocityX += (pointer.targetVelocityX - pointer.velocityX) * (reducedMotion ? 1 : 0.18);
    pointer.velocityY += (pointer.targetVelocityY - pointer.velocityY) * (reducedMotion ? 1 : 0.18);
    pointer.strength += (pointer.targetStrength - pointer.strength) * (reducedMotion ? 1 : 0.14);

    gl.useProgram(program);
    gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer);
    gl.enableVertexAttribArray(positionAttribute);
    gl.vertexAttribPointer(positionAttribute, 2, gl.FLOAT, false, 0, 0);
    gl.viewport(0, 0, canvas.width, canvas.height);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.clearColor(0, 0, 0, 0);
    gl.clear(gl.COLOR_BUFFER_BIT);
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, texture);
    gl.uniform1i(uniforms.u_logo, 0);
    gl.uniform2f(uniforms.u_resolution, canvas.width, canvas.height);
    gl.uniform2f(uniforms.u_logo_resolution, logoWidth, logoHeight);
    gl.uniform1f(uniforms.u_logo_draw_scale, measureLogoDrawScale());
    gl.uniform1f(uniforms.u_time, reducedMotion ? 0 : timestamp * 0.001);
    setUniformColor(gl, uniforms.u_mark, palette.mark || palette.textPrimary || palette.wisp);
    setUniformColor(gl, uniforms.u_wisp, palette.wisp);
    setUniformColor(gl, uniforms.u_logo_pastel_a, palette.logoPastelA || palette.accent);
    setUniformColor(gl, uniforms.u_logo_pastel_b, palette.logoPastelB || palette.logoPastelA || palette.accent);
    setUniformColor(gl, uniforms.u_logo_pastel_c, palette.logoPastelC || palette.logoPastelB || palette.accent);
    gl.uniform2f(uniforms.u_pointer, pointer.x, pointer.y);
    gl.uniform2f(uniforms.u_pointer_velocity, pointer.velocityX, pointer.velocityY);
    gl.uniform1f(uniforms.u_pointer_strength, reducedMotion ? 0 : pointer.strength);
    gl.drawArrays(gl.TRIANGLES, 0, 3);
  };

  const handlePointerMove = (event) => {
    const bounds = canvas.getBoundingClientRect();
    const now = performance.now();

    if (bounds.width <= 0 || bounds.height <= 0) {
      return now;
    }

    const nextX = (event.clientX - bounds.left) / bounds.width;
    const nextY = 1 - ((event.clientY - bounds.top) / bounds.height);
    const outsideDistance = distanceToUnitSquare(nextX, nextY);
    const previousMove = pointer.lastMove || now - 16;
    const elapsed = Math.max(16, now - previousMove);
    const deltaX = nextX - pointer.targetX;
    const deltaY = nextY - pointer.targetY;
    const nextVelocityX = clampVelocity(deltaX / (elapsed / 1000));
    const nextVelocityY = clampVelocity(deltaY / (elapsed / 1000));

    pointer.targetX = nextX;
    pointer.targetY = nextY;
    pointer.targetVelocityX += (nextVelocityX - pointer.targetVelocityX) * POINTER_VELOCITY_FOLLOW;
    pointer.targetVelocityY += (nextVelocityY - pointer.targetVelocityY) * POINTER_VELOCITY_FOLLOW;
    pointer.targetStrength = Math.max(0, 1 - outsideDistance * 1.55);
    pointer.lastMove = now;

    return now;
  };

  const handlePointerLeave = () => {
    pointer.targetStrength = 0;
    pointer.targetVelocityX = 0;
    pointer.targetVelocityY = 0;
  };

  const dispose = () => {
    gl.deleteTexture(texture);
    gl.deleteBuffer(positionBuffer);
    gl.deleteProgram(program);
  };

  return {
    dispose,
    drawFrame,
    handlePointerLeave,
    handlePointerMove,
  };
}
